import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { UpdateCardRequestSchema, type UpdateCardRequest } from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { toast } from "@/components/ui/notify";
import { isApiClientError } from "@/lib/api-client";
import { useUpdateCard, type CardSummary } from "@/features/cards/hooks";

interface CardEditFormProps {
  deviceId: string;
  card: CardSummary;
  onDirtyChange: (dirty: boolean) => void;
}

export function CardEditForm({ deviceId, card, onDirtyChange }: CardEditFormProps) {
  const update = useUpdateCard(deviceId);
  const form = useForm<UpdateCardRequest>({
    resolver: zodResolver(UpdateCardRequestSchema),
    defaultValues: {
      code: card.code,
      type: card.type,
      separator: card.separator ?? undefined,
      name: card.name ?? undefined,
    },
  });
  const dirty = form.formState.isDirty;
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
  const type = form.watch("type");

  async function onSubmit(values: UpdateCardRequest) {
    if (type === "ANALOG" && !values.separator) {
      form.setError("separator", { message: "Cartão analógico exige um separador." });
      return;
    }
    const payload: UpdateCardRequest = {
      code: values.code,
      type: values.type,
      separator: type === "ANALOG" ? values.separator : null,
      name: values.name ?? null,
    };
    try {
      await update.mutateAsync({ id: card.id, data: payload });
      toast.success("Cartão atualizado.");
      // Re-baseline com o que foi persistido (DIGITAL zera separador), não os valores crus do form.
      form.reset({
        code: payload.code,
        type: payload.type,
        separator: payload.separator ?? undefined,
        name: payload.name ?? undefined,
      });
    } catch (err) {
      toast.error("Falha ao salvar.", {
        description: isApiClientError(err) ? err.error.message : "Erro inesperado.",
      });
    }
  }

  const errs = form.formState.errors;
  return (
    <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)} className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="card-code">Código</Label>
          <Input id="card-code" {...form.register("code")} />
          {errs.code && <p className="text-xs text-destructive">{errs.code.message}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="card-type">Tipo</Label>
          <Select id="card-type" {...form.register("type")}>
            <option value="DIGITAL">Digital</option>
            <option value="ANALOG">Analógico</option>
          </Select>
        </div>
        {type === "ANALOG" && (
          <div className="space-y-1.5">
            <Label htmlFor="card-sep">Separador</Label>
            <Input id="card-sep" {...form.register("separator")} placeholder="#" />
            {errs.separator && <p className="text-xs text-destructive">{errs.separator.message}</p>}
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="card-name">Nome (opcional)</Label>
          <Input id="card-name" {...form.register("name")} />
        </div>
      </div>
      <Button type="submit" size="sm" disabled={!dirty || update.isPending}>
        {update.isPending ? "Salvando…" : "Salvar"}
      </Button>
    </form>
  );
}
